import { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { APPosting, ErpFieldValue, MatchDetailsResponse, TenantErpFieldConfigItem } from '@schemas/api';
import { useApproveAPPosting, useAPPostingWorkflowInfo, useCorrectAPPosting, useDiscardAPPosting, useReviewLineItems } from '@api/hooks/useAPPostings';
import { useApprovalRequestDetails } from '@api/hooks/useApprovalRequests';
import { useOnboardingConfig } from '@api/hooks/useOnboarding';
import { Button } from '@components/common/Button';
import { SearchableSelect } from '@components/common/SearchableSelect';
import { ApprovalChainReviewUI } from '@components/Approvals/ApprovalChainReviewUI';
import { useNotificationStore } from '@store/notificationStore';
import { useAPPostingWorkflowStore } from '@store/apPostingWorkflowStore';
import { useAuthStore } from '@store/authStore';
import { useGetErpConnections } from '@api/hooks/useErpConnection';
import { X, ChevronDown, Search } from 'lucide-react';
import { DynamicErpBulkFieldPicker, DynamicErpLineFieldCell, QBItemCell, QBAccountCell } from '@components/Review/LineItemsEditor';
import { useSearchQBCustomers } from '@api/hooks/useQBItems';
import { useDebouncedValue } from '../../hooks/useDebouncedValue';
import { useDocumentMatchDetails } from '@api/hooks/usePendingDocuments';
import type { QBCustomer } from '@schemas/api';
import { DynamicErpFieldsEditor } from '@components/ERP/DynamicErpFieldsEditor';
import {
  useErpFieldCatalog,
  useErpFieldConfig,
  useUpdateErpFieldConfig,
} from '@api/hooks/useErpFields';
import {
  getAvailableReviewErpFields,
  isHeaderOnlyReviewErpField,
  isReviewErpField,
} from '@/lib/reviewErpFields';
import { APPostingErrorAlert } from './APPostingErrorAlert';
import { formatDate } from '@utils/formatters';

type ReviewPanelTab = 'posting' | 'discrepancies';
type PostingLineType = 'item' | 'category';
type BulkPostingLineFieldKey = 'item' | 'category' | string;

interface DiscrepancyComparisonRow {
  metric: string;
  invoice: string | null;
  po: string | null;
  receipt: string | null;
}

interface DiscrepancyBreakdown {
  rows: DiscrepancyComparisonRow[];
  extras: Array<{ key: string; value: string }>;
}

const formatDiscrepancyLabel = (value: string): string =>
  value
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());

const toDisplayNumber = (value: unknown): number | null => {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  const parsed = typeof value === 'number' ? value : Number(String(value));
  return Number.isFinite(parsed) ? parsed : null;
};

const formatCurrencyValue = (value: unknown): string | null => {
  const numericValue = toDisplayNumber(value);
  if (numericValue === null) {
    return null;
  }
  return `$${numericValue.toFixed(2)}`;
};

const formatQuantityValue = (value: unknown): string | null => {
  const numericValue = toDisplayNumber(value);
  if (numericValue === null) {
    return null;
  }
  return Number.isInteger(numericValue) ? String(numericValue) : numericValue.toFixed(4);
};

const formatPercentValue = (value: unknown): string | null => {
  const numericValue = toDisplayNumber(value);
  if (numericValue === null) {
    return null;
  }
  const percent = Math.abs(numericValue) <= 1 ? numericValue * 100 : numericValue;
  return `${percent.toFixed(2)}%`;
};

const formatRawValue = (value: unknown): string | null => {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  if (typeof value === 'object') {
    try {
      return JSON.stringify(value);
    } catch {
      return String(value);
    }
  }
  return String(value);
};

const lineValueKey = (fieldKey: string, lineId?: string | null, lineNumber?: number) =>
  `${fieldKey}:${lineId || `line-${lineNumber ?? ''}`}`;

const normalizedLineNumber = (value?: unknown) =>
  Number.isFinite(Number(value)) ? Number(value) : undefined;

const evidenceLineNumber = (value: ErpFieldValue) =>
  normalizedLineNumber(value.evidence?.line_number);

const lineValueKeys = (fieldKey: string, lineId?: string | null, lineNumber?: number | null) => {
  const normalizedNumber = normalizedLineNumber(lineNumber);
  const keys = [lineValueKey(fieldKey, lineId, normalizedNumber)];
  if (normalizedNumber !== undefined) {
    keys.push(lineValueKey(fieldKey, null, normalizedNumber));
  }
  return keys;
};

const erpValueLineKeys = (value: ErpFieldValue) =>
  lineValueKeys(value.field_key, value.line_id, evidenceLineNumber(value));

const addDynamicValueLookup = (lookup: Map<string, ErpFieldValue>, value: ErpFieldValue) => {
  erpValueLineKeys(value).forEach((key) => lookup.set(key, value));
};

const getDynamicLineValue = (
  lookup: Map<string, ErpFieldValue>,
  fieldKey: string,
  lineId?: string | null,
  lineNumber?: number | null,
) => {
  for (const key of lineValueKeys(fieldKey, lineId, lineNumber)) {
    const value = lookup.get(key);
    if (value) {
      return value;
    }
  }
  return undefined;
};

const classifyVarianceDetailKey = (key: string): 'invoice' | 'po' | 'receipt' | 'other' => {
  const normalized = key.toLowerCase();
  if (
    normalized.startsWith('inv_') ||
    normalized.includes('invoice') ||
    normalized.startsWith('actual_') ||
    normalized === 'actual_tax'
  ) {
    return 'invoice';
  }
  if (
    normalized.startsWith('po_') ||
    normalized.includes('expected') ||
    normalized.includes('ordered')
  ) {
    return 'po';
  }
  if (
    normalized.startsWith('receipt') ||
    normalized.startsWith('received') ||
    normalized.includes('receipt')
  ) {
    return 'receipt';
  }
  return 'other';
};

const buildDiscrepancyBreakdown = (variance: Record<string, unknown>): DiscrepancyBreakdown => {
  const details = (variance.details && typeof variance.details === 'object'
    ? variance.details
    : {}) as Record<string, unknown>;
  const rows: DiscrepancyComparisonRow[] = [];
  const extras: Array<{ key: string; value: string }> = [];
  const consumedKeys = new Set<string>();
  const varianceType = String(variance.type || '').toLowerCase();

  const pushRow = (
    metric: string,
    invoice: string | null,
    po: string | null,
    receipt: string | null,
    keys: string[] = [],
  ) => {
    if (!invoice && !po && !receipt) {
      return;
    }
    rows.push({ metric, invoice, po, receipt });
    keys.forEach((key) => consumedKeys.add(key));
  };

  if (varianceType === 'qty_variance') {
    pushRow(
      'Quantity',
      formatQuantityValue(details.invoiced_qty),
      formatQuantityValue(details.ordered_qty),
      formatQuantityValue(details.received_qty),
      ['invoiced_qty', 'ordered_qty', 'received_qty'],
    );
    pushRow(
      'Quantity Delta',
      formatQuantityValue(details.delta),
      null,
      null,
      ['delta'],
    );
    pushRow(
      'Quantity Variance %',
      formatPercentValue(details.pct_delta),
      null,
      null,
      ['pct_delta'],
    );
  } else if (varianceType === 'price_variance') {
    pushRow(
      'Unit Price',
      formatCurrencyValue(details.inv_unit_price),
      formatCurrencyValue(details.po_unit_price),
      null,
      ['inv_unit_price', 'po_unit_price'],
    );
    pushRow(
      'Price Delta',
      formatCurrencyValue(details.delta_abs),
      null,
      null,
      ['delta_abs'],
    );
    pushRow(
      'Price Variance %',
      formatPercentValue(details.delta_pct),
      null,
      null,
      ['delta_pct'],
    );
  } else if (varianceType === 'tax_discrepancy') {
    pushRow(
      'Tax Amount',
      formatCurrencyValue(details.actual_tax),
      formatCurrencyValue(details.expected_tax),
      null,
      ['actual_tax', 'expected_tax'],
    );
    pushRow(
      'Tax Delta',
      formatCurrencyValue(details.delta),
      null,
      null,
      ['delta'],
    );
  }

  Object.entries(details).forEach(([key, value]) => {
    if (consumedKeys.has(key)) {
      return;
    }
    const displayValue = formatRawValue(value);
    if (!displayValue) {
      return;
    }
    const keyLabel = formatDiscrepancyLabel(key);
    const column = classifyVarianceDetailKey(key);
    if (column === 'invoice') {
      pushRow(keyLabel, displayValue, null, null, [key]);
      return;
    }
    if (column === 'po') {
      pushRow(keyLabel, null, displayValue, null, [key]);
      return;
    }
    if (column === 'receipt') {
      pushRow(keyLabel, null, null, displayValue, [key]);
      return;
    }
    extras.push({ key: keyLabel, value: displayValue });
    consumedKeys.add(key);
  });

  return { rows, extras };
};

interface APPostingApprovalModalProps {
  posting: APPosting;
  isOpen: boolean;
  onClose: () => void;
  variant?: 'modal' | 'embedded';
  onReviewComplete?: () => void;
}

export const APPostingApprovalModal: React.FC<APPostingApprovalModalProps> = ({
  posting,
  isOpen,
  onClose,
  variant = 'modal',
  onReviewComplete,
}) => {
  const queryClient = useQueryClient();
  const [applyDiscount, setApplyDiscount] = useState(false);
  const [discountAccountRef, setDiscountAccountRef] = useState<string>('');
  const [discountAccountName, setDiscountAccountName] = useState<string>('');
  const [approvalNotes, setApprovalNotes] = useState('');
  const [invoiceNumber, setInvoiceNumber] = useState(posting.invoice_num);
  const [showDiscardForm, setShowDiscardForm] = useState(false);
  const [discardReason, setDiscardReason] = useState('');
  const [approvalRequestId, setApprovalRequestId] = useState<string | null>(null);
  const [showApprovalChain, setShowApprovalChain] = useState(false);
  // Track ERP item selections per line: ap_line_id -> { erp_item_id, erp_item_name }
  const [itemSelections, setItemSelections] = useState<Map<string, { erp_item_id: string; erp_item_name: string }>>(new Map());
  // Track account selections for synthetic (account-based) lines
  const [accountSelections, setAccountSelections] = useState<Map<string, { account_id: string; account_name: string }>>(new Map());
  const [linePostingTypes, setLinePostingTypes] = useState<Map<string, PostingLineType>>(new Map());
  // Header-only bill toggle
  const [headerOnly, setHeaderOnly] = useState(true);
  const [headerOnlyAccountRef, setHeaderOnlyAccountRef] = useState<string>('');
  const [headerOnlyAccountName, setHeaderOnlyAccountName] = useState<string>('');
  const [headerOnlyLineDescription, setHeaderOnlyLineDescription] = useState('');
  const [billMemo, setBillMemo] = useState('');
  const [selectedTaxCodeRef, setSelectedTaxCodeRef] = useState<string>('');
  const [dynamicErpValues, setDynamicErpValues] = useState<ErpFieldValue[]>([]);
  // Customer/project selection
  const [customerProjectId, setCustomerProjectId] = useState<string>('');
  const [customerProjectName, setCustomerProjectName] = useState<string>('');
  const [selectedBulkLineIds, setSelectedBulkLineIds] = useState<Set<string>>(new Set());
  const [bulkPostingLineFieldKey, setBulkPostingLineFieldKey] = useState<BulkPostingLineFieldKey>('item');
  const [activeReviewTab, setActiveReviewTab] = useState<ReviewPanelTab>('posting');
  const [selectedHeaderErpFieldId, setSelectedHeaderErpFieldId] = useState('');
  const [selectedLineErpFieldId, setSelectedLineErpFieldId] = useState('');
  const [selectedHeaderOnlyErpFieldId, setSelectedHeaderOnlyErpFieldId] = useState('');
  const [selectedErpConnectionId, setSelectedErpConnectionId] = useState('');

  const lineItemsQuery = useReviewLineItems(posting.id, selectedErpConnectionId || undefined);
  const { data: lineItemsData, isLoading: isLoadingLineItems } = lineItemsQuery;
  const { data: erpConnections = [] } = useGetErpConnections(isOpen);
  const selectedErpConnection = useMemo(
    () => erpConnections.find((connection) => connection.id === selectedErpConnectionId),
    [erpConnections, selectedErpConnectionId],
  );
  const canCustomizeBillMemo = selectedErpConnection?.erp_type === 'quickbooks_desktop';
  const isQuickBooksOnline = selectedErpConnection?.erp_type === 'quickbooks';
  const matchDetailsDocumentId = isOpen ? posting.invoice_id : undefined;
  const invoiceMatchDetailsQuery = useDocumentMatchDetails('invoice', matchDetailsDocumentId);
  const workflowInfoQuery = useAPPostingWorkflowInfo(posting.id, isOpen);
  const hasSelectedErpConnection = Boolean(selectedErpConnectionId);
  const apPostingErpCatalogQuery = useErpFieldCatalog('ap_posting', isOpen && hasSelectedErpConnection, selectedErpConnectionId || undefined);
  const apPostingErpConfigQuery = useErpFieldConfig('ap_posting', isOpen && hasSelectedErpConnection, selectedErpConnectionId || undefined);
  const updateErpFieldConfig = useUpdateErpFieldConfig(selectedErpConnectionId || undefined);
  const workflowInfo = workflowInfoQuery.data;
  const postingError = workflowInfo?.posting_error ?? posting.posting_error;
  const invoiceMatchDetails = invoiceMatchDetailsQuery.data;
  const invoiceDiscrepancies = useMemo(
    () => (invoiceMatchDetails?.variances || []).filter((variance): variance is Record<string, unknown> => Boolean(variance)),
    [invoiceMatchDetails?.variances],
  );
  const hasMatchedDocumentsForDiscrepancyReview = useMemo(() => {
    if (!invoiceMatchDetails) {
      return false;
    }
    const hasPo = Boolean(invoiceMatchDetails.linked_po || invoiceMatchDetails.selected_po);
    const waitingForCounterpart = (invoiceMatchDetails.waiting_for?.length || 0) > 0;
    return hasPo && !waitingForCounterpart;
  }, [invoiceMatchDetails]);
  const hasDiscrepancyTab = hasMatchedDocumentsForDiscrepancyReview && invoiceDiscrepancies.length > 0;
  const lineAllocationByInvoiceLine = useMemo(() => {
    const allocations = invoiceMatchDetails?.line_allocations || [];
    const allocationMap = new Map<string, Record<string, unknown>>();
    allocations.forEach((entry) => {
      const allocation = entry as Record<string, unknown>;
      const key = String(allocation.invoice_line_number ?? allocation.invoice_line_id ?? '').trim();
      if (key && !allocationMap.has(key)) {
        allocationMap.set(key, allocation);
      }
    });
    return allocationMap;
  }, [invoiceMatchDetails?.line_allocations]);
  const approvalRequestQuery = useApprovalRequestDetails(approvalRequestId);
  const workflowId = workflowInfo?.workflow_id ?? null;
  const approvalStatus = approvalRequestQuery.data?.status ?? workflowInfo?.approval_status ?? null;
  const matchedApprovalChainId = workflowInfo?.approval_chain_id ?? null;
  // Every failed posting uses the correction workflow. This also covers a first-time ERP
  // rejection where no bill reference exists yet, allowing the user to fix coding and retry.
  const isPostedCorrection =
    posting.status === 'posting_failed' || (Boolean(posting.erp_reference) && posting.status === 'posted');
  const hasApprovalChainView = Boolean(approvalRequestId) && !isPostedCorrection;
  const isTerminalApprovalChain =
    hasApprovalChainView &&
    (approvalStatus === 'approved' || approvalStatus === 'rejected');
  const isPendingApprovalChain = hasApprovalChainView && !isTerminalApprovalChain;
  const canEditPendingApprovalReview = isPendingApprovalChain;
  const areReviewFieldsLocked = hasApprovalChainView ? !canEditPendingApprovalReview : false;
  const arePostingStructureControlsLocked = hasApprovalChainView ? !canEditPendingApprovalReview : false;
  const isApprovalRequiredBeforeSubmission =
    !approvalRequestId &&
    Boolean(matchedApprovalChainId) &&
    workflowInfo?.status === 'pending_approval';
  const effectiveHeaderOnly = headerOnly;

  // Pre-populate customer/project from auto-matched value on the AP posting
  useEffect(() => {
    if (lineItemsData?.customer_project_id && !customerProjectId) {
      setCustomerProjectId(lineItemsData.customer_project_id);
      setCustomerProjectName(
        lineItemsData.customer_project_fqn || lineItemsData.customer_project_name || lineItemsData.customer_project_id
      );
    }
  }, [lineItemsData?.customer_project_id]);

  useEffect(() => {
    if (!isOpen || !lineItemsData) {
      return;
    }
    setHeaderOnly(Boolean(lineItemsData.is_header_only_mode));
    setApplyDiscount(Boolean(lineItemsData.is_discount_applied));
    setDiscountAccountRef(lineItemsData.discount_account_id || '');
    setDiscountAccountName(lineItemsData.discount_account_name || '');
    setSelectedTaxCodeRef((lineItemsData.selected_tax_code_refs || [])[0] || '');
    setHeaderOnlyLineDescription(
      lineItemsData.header_only_line_description || `Invoice ${posting.invoice_num} total`,
    );
    setBillMemo(lineItemsData.bill_memo || '');
    setDynamicErpValues(lineItemsData.dynamic_erp_fields?.values || []);
  }, [
    isOpen,
    posting.id,
    posting.invoice_num,
    lineItemsData,
  ]);

  useEffect(() => {
    if (isOpen) {
      setInvoiceNumber(posting.invoice_num);
    }
  }, [isOpen, posting.id, posting.invoice_num]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    const responseConnectionId = lineItemsData?.dynamic_erp_fields?.erp_connection_id;
    const availableIds = new Set(erpConnections.map((connection) => connection.id));
    const postedConnectionId = isPostedCorrection && posting.posted_erp_type && posting.posted_realm_id
      ? erpConnections.find((connection) =>
          connection.erp_type === posting.posted_erp_type && connection.realm_id === posting.posted_realm_id
        )?.id
      : undefined;
    const nextConnectionId =
      postedConnectionId
      || (responseConnectionId && availableIds.has(responseConnectionId) ? responseConnectionId : undefined)
      || '';
    setSelectedErpConnectionId((current) => current && availableIds.has(current) ? current : nextConnectionId);
  }, [isOpen, isPostedCorrection, posting.posted_erp_type, posting.posted_realm_id, lineItemsData?.dynamic_erp_fields?.erp_connection_id, erpConnections]);

  useEffect(() => {
    if (!lineItemsData?.line_items?.length) {
      return;
    }

    setItemSelections(() => {
      const next = new Map<string, { erp_item_id: string; erp_item_name: string }>();
      for (const item of lineItemsData.line_items) {
        if (item.erp_item_id) {
          next.set(item.ap_line_id, {
            erp_item_id: item.erp_item_id,
            erp_item_name: item.erp_item_name || item.erp_item_id,
          });
        }
      }
      return next;
    });

    setAccountSelections(() => {
      const next = new Map<string, { account_id: string; account_name: string }>();
      for (const item of lineItemsData.line_items) {
        if (item.erp_account_id) {
          next.set(item.ap_line_id, {
            account_id: item.erp_account_id,
            account_name: item.erp_account_name || item.erp_account_id,
          });
        }
      }
      return next;
    });

    if (lineItemsData.is_header_only_mode) {
      const headerLine = lineItemsData.line_items[0];
      if (headerLine?.erp_account_id) {
        setHeaderOnlyAccountRef(headerLine.erp_account_id);
        setHeaderOnlyAccountName(headerLine.erp_account_name || headerLine.erp_account_id);
      }
    }

    setLinePostingTypes(() => {
      const next = new Map<string, PostingLineType>();
      for (const item of lineItemsData.line_items) {
        next.set(
          item.ap_line_id,
          item.is_synthetic || (Boolean(item.erp_account_id) && !item.erp_item_id) ? 'category' : 'item',
        );
      }
      return next;
    });
  }, [lineItemsData?.line_items]);

  useEffect(() => {
    if (isPostedCorrection) {
      setApprovalRequestId(null);
      setShowApprovalChain(false);
      setShowDiscardForm(false);
      setDiscardReason('');
      return;
    }
    const activeApprovalRequestId = workflowInfo?.approval_request_id;
    if (activeApprovalRequestId) {
      setApprovalRequestId(activeApprovalRequestId);
      setShowDiscardForm(false);
      setDiscardReason('');
    } else {
      setApprovalRequestId(null);
      setShowApprovalChain(false);
    }
  }, [isPostedCorrection, workflowInfo?.approval_request_id]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    setShowApprovalChain(false);
  }, [isOpen, posting.id]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    setSelectedBulkLineIds(new Set());
  }, [isOpen, posting.id]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    setActiveReviewTab('posting');
  }, [isOpen, posting.id]);

  useEffect(() => {
    if (hasDiscrepancyTab) {
      return;
    }
    setActiveReviewTab('posting');
  }, [hasDiscrepancyTab]);

  const approveMutation = useApproveAPPosting();
  const correctMutation = useCorrectAPPosting();
  const discardMutation = useDiscardAPPosting();
  const { addNotification } = useNotificationStore();
  const trackWorkflow = useAPPostingWorkflowStore((state) => state.trackWorkflow);
  const hasErpIntegration = Boolean(selectedErpConnectionId);

  const { data: onboardingConfig } = useOnboardingConfig();
  const currentUser = useAuthStore((state) => state.currentUser);

  const billFields = onboardingConfig?.bill_creation_fields ?? [];

  // Returns true if a header-level field key is enabled
  const isBillFieldEnabled = (key: string) => billFields.includes(key);

  // Line item column names from config (strip prefix), with fallback
  const configLineItemCols = billFields
    .filter((k) => k.startsWith('line_items.'))
    .map((k) => k.replace('line_items.', ''));

  const lineItemCols = configLineItemCols.length > 0
    ? configLineItemCols
    : ['description', 'quantity', 'unit_price', 'total'];

  const getUserId = () => {
    return currentUser?.id || currentUser?.email || 'system@example.com';
  };

  const handleApprovalChainComplete = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['ap-postings'] });
    workflowInfoQuery.refetch();
    approvalRequestQuery.refetch();
    setShowApprovalChain(true);
  }, [approvalRequestQuery, queryClient, workflowInfoQuery]);

  const handleApprovalChainRejected = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['ap-postings'] });
    workflowInfoQuery.refetch();
    approvalRequestQuery.refetch();
    setShowApprovalChain(true);
  }, [approvalRequestQuery, queryClient, workflowInfoQuery]);

  const handleItemSelect = useCallback((apLineId: string, erpItemId: string, erpItemName: string) => {
    setItemSelections((prev) => {
      const next = new Map(prev);
      next.set(apLineId, { erp_item_id: erpItemId, erp_item_name: erpItemName });
      return next;
    });
    setAccountSelections((prev) => {
      const next = new Map(prev);
      next.delete(apLineId);
      return next;
    });
    setLinePostingTypes((prev) => {
      const next = new Map(prev);
      next.set(apLineId, 'item');
      return next;
    });
  }, []);

  const handleItemClear = useCallback((apLineId: string) => {
    setItemSelections((prev) => {
      const next = new Map(prev);
      next.delete(apLineId);
      return next;
    });
  }, []);

  const handleToggleBulkLine = useCallback((apLineId: string) => {
    setSelectedBulkLineIds((prev) => {
      const next = new Set(prev);
      if (next.has(apLineId)) {
        next.delete(apLineId);
      } else {
        next.add(apLineId);
      }
      return next;
    });
  }, []);

  const handleBulkItemSelect = useCallback((itemId: string, itemName: string) => {
    if (selectedBulkLineIds.size === 0) {
      return;
    }
    setItemSelections((prev) => {
      const next = new Map(prev);
      selectedBulkLineIds.forEach((apLineId) => {
        next.set(apLineId, { erp_item_id: itemId, erp_item_name: itemName });
      });
      return next;
    });
    setAccountSelections((prev) => {
      const next = new Map(prev);
      selectedBulkLineIds.forEach((apLineId) => {
        next.delete(apLineId);
      });
      return next;
    });
    setLinePostingTypes((prev) => {
      const next = new Map(prev);
      selectedBulkLineIds.forEach((apLineId) => {
        next.set(apLineId, 'item');
      });
      return next;
    });
  }, [selectedBulkLineIds]);

  const handleBulkItemClear = useCallback(() => {
    if (selectedBulkLineIds.size === 0) {
      return;
    }
    setItemSelections((prev) => {
      const next = new Map(prev);
      selectedBulkLineIds.forEach((apLineId) => {
        next.delete(apLineId);
      });
      return next;
    });
  }, [selectedBulkLineIds]);

  const handleBulkAccountSelect = useCallback((accountId: string, accountName: string) => {
    if (selectedBulkLineIds.size === 0) {
      return;
    }
    setAccountSelections((prev) => {
      const next = new Map(prev);
      selectedBulkLineIds.forEach((apLineId) => {
        next.set(apLineId, { account_id: accountId, account_name: accountName });
      });
      return next;
    });
    setItemSelections((prev) => {
      const next = new Map(prev);
      selectedBulkLineIds.forEach((apLineId) => {
        next.delete(apLineId);
      });
      return next;
    });
    setLinePostingTypes((prev) => {
      const next = new Map(prev);
      selectedBulkLineIds.forEach((apLineId) => {
        next.set(apLineId, 'category');
      });
      return next;
    });
  }, [selectedBulkLineIds]);

  const handleBulkAccountClear = useCallback(() => {
    if (selectedBulkLineIds.size === 0) {
      return;
    }
    setAccountSelections((prev) => {
      const next = new Map(prev);
      selectedBulkLineIds.forEach((apLineId) => {
        next.delete(apLineId);
      });
      return next;
    });
  }, [selectedBulkLineIds]);

  const handleAccountSelect = useCallback((apLineId: string, accountId: string, accountName: string) => {
    setAccountSelections((prev) => {
      const next = new Map(prev);
      next.set(apLineId, { account_id: accountId, account_name: accountName });
      return next;
    });
    setItemSelections((prev) => {
      const next = new Map(prev);
      next.delete(apLineId);
      return next;
    });
    setLinePostingTypes((prev) => {
      const next = new Map(prev);
      next.set(apLineId, 'category');
      return next;
    });
  }, []);

  const handleAccountClear = useCallback((apLineId: string) => {
    setAccountSelections((prev) => {
      const next = new Map(prev);
      next.delete(apLineId);
      return next;
    });
  }, []);

  // Get the effective erp_item_id for a line (user selection takes priority over existing)
  const getEffectiveErpItemId = useCallback((apLineId: string, existingErpItemId?: string) => {
    const selection = itemSelections.get(apLineId);
    if (selection) return selection.erp_item_id;
    return existingErpItemId || null;
  }, [itemSelections]);

  const getEffectiveErpAccountId = useCallback((apLineId: string, existingErpAccountId?: string) => {
    const selection = accountSelections.get(apLineId);
    if (selection) return selection.account_id;
    return existingErpAccountId || null;
  }, [accountSelections]);

  const getLinePostingType = useCallback((item: { ap_line_id: string; is_synthetic?: boolean; erp_account_id?: string; erp_item_id?: string }): PostingLineType => {
    if (item.is_synthetic) {
      return 'category';
    }
    return linePostingTypes.get(item.ap_line_id) || (item.erp_account_id && !item.erp_item_id ? 'category' : 'item');
  }, [linePostingTypes]);

  const handleLinePostingTypeChange = useCallback((apLineId: string, nextType: PostingLineType) => {
    setLinePostingTypes((prev) => {
      const next = new Map(prev);
      next.set(apLineId, nextType);
      return next;
    });
    if (nextType === 'item') {
      setAccountSelections((prev) => {
        const next = new Map(prev);
        next.delete(apLineId);
        return next;
      });
    } else {
      setItemSelections((prev) => {
        const next = new Map(prev);
        next.delete(apLineId);
        return next;
      });
    }
  }, []);

  const updateLineDynamicErpValue = useCallback((nextValue: ErpFieldValue, lineNumber?: number) => {
    const nextKeys = lineValueKeys(nextValue.field_key, nextValue.line_id, lineNumber);
    setDynamicErpValues((prev) => [
      ...prev.filter((value) => (
        !erpValueLineKeys(value).some((key) => nextKeys.includes(key))
      )),
      nextValue,
    ]);
  }, []);

  const updateHeaderOnlyDynamicErpValue = useCallback((nextValue: ErpFieldValue) => {
    setDynamicErpValues((prev) => [
      ...prev.filter((value) => value.field_key !== nextValue.field_key),
      nextValue,
    ]);
  }, []);

  // Whether invoice had no line items (all synthetic from backend)
  const allLinesSynthetic = (lineItemsData?.line_items || []).length > 0
    && (lineItemsData?.line_items || []).every((item) => item.is_synthetic);

  // Show the header-only toggle only when the invoice actually has real line items
  const showHeaderOnlyToggle =
    Boolean(lineItemsData?.can_toggle_header_only) && (lineItemsData?.line_items || []).length > 0;

  const allLinesLinked = effectiveHeaderOnly
    ? Boolean(headerOnlyAccountRef)
    : (lineItemsData?.line_items || []).every(
        (item) => getLinePostingType(item) === 'category'
          ? Boolean(getEffectiveErpAccountId(item.ap_line_id, item.erp_account_id))
          : getEffectiveErpItemId(item.ap_line_id, item.erp_item_id)
      );

  const handleApprove = async () => {
    const normalizedInvoiceNumber = invoiceNumber.trim();
    const isInvoiceNumberChanged = normalizedInvoiceNumber !== posting.invoice_num;
    if (!normalizedInvoiceNumber) {
      addNotification({ message: 'Please enter an invoice number', type: 'warning' });
      return;
    }
    if (isQuickBooksOnline && normalizedInvoiceNumber.length > 21) {
      addNotification({
        message: 'QuickBooks Online bill reference numbers must be 21 characters or fewer',
        type: 'warning',
      });
      return;
    }
    if (!hasApprovalChainView && applyDiscount && !discountAccountRef) {
      addNotification({ message: 'Please select an account for the discount line', type: 'warning' });
      return;
    }

    if (!allLinesLinked) {
      addNotification({ message: 'Please link all line items to an ERP item or account before approving', type: 'warning' });
      return;
    }

    if (effectiveHeaderOnly && !headerOnlyLineDescription.trim()) {
      addNotification({ message: 'Please enter a description for the header-only bill line', type: 'warning' });
      return;
    }

    try {
      let response: { approval_request_id?: string | null; corrected_fields?: string[] } | undefined;
      const submitPosting = isPostedCorrection ? correctMutation : approveMutation;

      // Header-only mode: send a single account ref, no per-line decisions
      if (effectiveHeaderOnly) {
        response = await submitPosting.mutateAsync({
          apPostingId: posting.id,
          request: {
            user: getUserId(),
            erp_connection_id: selectedErpConnectionId || undefined,
            invoice_number: isInvoiceNumberChanged ? normalizedInvoiceNumber : undefined,
            apply_discount: applyDiscount,
            discount_account_ref: discountAccountRef || undefined,
            header_only_account_ref: headerOnlyAccountRef,
            header_only_line_description: headerOnlyLineDescription.trim(),
            bill_memo: canCustomizeBillMemo ? (billMemo.trim() || null) : undefined,
            tax_code_refs: selectedTaxCodeRef ? [selectedTaxCodeRef] : [],
            customer_project_id: customerProjectId || undefined,
            approval_notes: approvalNotes || undefined,
            dynamic_erp_fields: pushableDynamicBillValues,
            line_items: [],
          },
        });
      } else {
        const lineItems = (lineItemsData?.line_items || []).map((item) => {
          if (getLinePostingType(item) === 'category') {
            const effectiveAccountId = getEffectiveErpAccountId(item.ap_line_id, item.erp_account_id);
            return {
              ap_line_id: item.ap_line_id,
              account_ref: effectiveAccountId || undefined,
            };
          }
          const erpItemId = getEffectiveErpItemId(item.ap_line_id, item.erp_item_id);
          return {
            ap_line_id: item.ap_line_id,
            selected_item_id: erpItemId || undefined,
          };
        });

        response = await submitPosting.mutateAsync({
          apPostingId: posting.id,
          request: {
            user: getUserId(),
            erp_connection_id: selectedErpConnectionId || undefined,
            invoice_number: isInvoiceNumberChanged ? normalizedInvoiceNumber : undefined,
            apply_discount: applyDiscount,
            discount_account_ref: discountAccountRef || undefined,
            bill_memo: canCustomizeBillMemo ? (billMemo.trim() || null) : undefined,
            tax_code_refs: selectedTaxCodeRef ? [selectedTaxCodeRef] : [],
            approval_notes: approvalNotes || undefined,
            dynamic_erp_fields: pushableDynamicBillValues,
            line_items: lineItems,
          },
        });
      }

      addNotification({
        message: isPostedCorrection
          ? 'Correction submitted for processing.'
          : 'Approval submitted. AP posting is being processed.',
        type: 'success',
      });
      if (isPostedCorrection) {
        const correctionWorkflowId = (response as { correction_workflow_id?: string | null })
          ?.correction_workflow_id;
        if (correctionWorkflowId) {
          trackWorkflow({
            workflowId: correctionWorkflowId,
            apPostingId: posting.id,
            invoiceNum: posting.invoice_num,
            action: 'correct',
          });
        }
      } else if (workflowId) {
        trackWorkflow({
          workflowId,
          apPostingId: posting.id,
          invoiceNum: posting.invoice_num,
          action: 'approve',
        });
      }
      queryClient.invalidateQueries({ queryKey: ['ap-postings'] });
      const workflowRefresh = isPostedCorrection ? null : await workflowInfoQuery.refetch();
      const activeApprovalRequestId =
        response?.approval_request_id ??
        workflowRefresh?.data?.approval_request_id ??
        null;

      if (activeApprovalRequestId) {
        setApprovalRequestId(activeApprovalRequestId);
        setShowDiscardForm(false);
        setDiscardReason('');
        setShowApprovalChain(true);
        return;
      }

      if (onReviewComplete) {
        onReviewComplete();
      } else {
        onClose();
      }
    } catch (error) {
      addNotification({
        message: isPostedCorrection ? 'Failed to correct AP posting' : 'Failed to approve AP posting',
        type: 'error',
      });
    }
  };

  const handleDiscard = async () => {
    if (!discardReason.trim()) {
      addNotification({ message: 'Please provide a reason for discarding', type: 'warning' });
      return;
    }

    try {
      await discardMutation.mutateAsync({
        apPostingId: posting.id,
        request: {
          user: getUserId(),
          reason: discardReason,
        },
      });

      addNotification({ message: 'Discard submitted. AP posting is being updated.', type: 'success' });
      if (workflowId) {
        trackWorkflow({
          workflowId,
          apPostingId: posting.id,
          invoiceNum: posting.invoice_num,
          action: 'discard',
        });
      }
      queryClient.invalidateQueries({ queryKey: ['ap-postings'] });
      onClose();
    } catch (error) {
      addNotification({ message: 'Failed to discard AP posting', type: 'error' });
    }
  };

  if (!isOpen) return null;

  // Inline customer/project picker (mirrors QBItemCell pattern)
  const CustomerProjectPicker: React.FC<{ disabled?: boolean }> = ({ disabled = false }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [search, setSearch] = useState('');
    const debouncedSearch = useDebouncedValue(search, 300);
    const containerRef = useRef<HTMLDivElement>(null);
    const { data, isLoading } = useSearchQBCustomers(
      debouncedSearch, 0, 50, selectedErpConnectionId || undefined,
    );
    const results = data?.customers || [];

    useEffect(() => {
      if (!isOpen) return;
      const handler = (e: MouseEvent) => {
        if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
          setIsOpen(false);
        }
      };
      document.addEventListener('mousedown', handler);
      return () => document.removeEventListener('mousedown', handler);
    }, [isOpen]);

    return (
      <div ref={containerRef} className="relative">
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => {
              if (!disabled) {
                setIsOpen(!isOpen);
              }
            }}
            disabled={disabled}
            className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-sm flex items-center justify-between gap-2 hover:border-gray-400 dark:hover:border-gray-500 focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <span className="truncate text-left text-gray-900 dark:text-gray-100">
              {customerProjectName || 'Select customer / project...'}
            </span>
            <ChevronDown className={`h-3.5 w-3.5 text-gray-400 flex-shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
          </button>
          {customerProjectId && !disabled && (
            <button
              type="button"
              onClick={() => { setCustomerProjectId(''); setCustomerProjectName(''); }}
              className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
              title="Clear"
            >
              <X className="h-3.5 w-3.5 text-gray-400" />
            </button>
          )}
        </div>

        {isOpen && (
          <div className="absolute top-full left-0 mt-1 w-full min-w-[280px] z-50 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg">
            <div className="p-2 border-b border-gray-200 dark:border-gray-700">
              <div className="relative">
                <Search className="absolute left-2 top-2 h-3.5 w-3.5 text-gray-400" />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search customers / projects..."
                  disabled={disabled}
                  className="w-full pl-7 pr-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  autoFocus
                />
              </div>
            </div>
            <div className="max-h-52 overflow-y-auto">
              {isLoading && (
                <div className="p-3 text-center text-xs text-gray-500 dark:text-gray-400">Loading...</div>
              )}
              {!isLoading && results.length === 0 && (
                <div className="p-3 text-center text-xs text-gray-500 dark:text-gray-400">
                  {search ? `No results for "${search}"` : 'Type to search'}
                </div>
              )}
              {results.map((c: QBCustomer) => (
                <button
                  key={c.id}
                  type="button"
                  disabled={disabled}
                  onClick={() => {
                    setCustomerProjectId(c.id);
                    setCustomerProjectName(c.fully_qualified_name || c.display_name);
                    setIsOpen(false);
                    setSearch('');
                  }}
                  className={`w-full text-left px-3 py-2 text-sm hover:bg-blue-50 dark:hover:bg-blue-900 border-b border-gray-100 dark:border-gray-700 last:border-b-0 ${
                    customerProjectId === c.id ? 'bg-blue-100 dark:bg-blue-800' : ''
                  }`}
                >
                  <p className="font-medium text-gray-900 dark:text-gray-100 truncate">{c.display_name}</p>
                  {c.fully_qualified_name !== c.display_name && (
                    <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{c.fully_qualified_name}</p>
                  )}
                  {c.is_project && (
                    <span className="text-xs text-blue-600 dark:text-blue-400">Project</span>
                  )}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  const hasDiscount = posting.discount_info?.has_discount;
  const discountPercent = posting.discount_info?.discount_percent || 0;
  const discountedAmount = posting.posting_amount * (discountPercent / 100);
  const lineItems = lineItemsData?.line_items || [];
  const codingLineIds = useMemo(
    () => lineItems
      .filter((item) => !item.is_synthetic)
      .map((item) => item.ap_line_id),
    [lineItems],
  );
  const selectedCodingCount = codingLineIds.filter((apLineId) => selectedBulkLineIds.has(apLineId)).length;
  const allCodingSelected = codingLineIds.length > 0 && selectedCodingCount === codingLineIds.length;

  useEffect(() => {
    if (codingLineIds.length === 0 && selectedBulkLineIds.size === 0) {
      return;
    }
    const validIds = new Set(codingLineIds);
    setSelectedBulkLineIds((prev) => {
      const next = new Set(Array.from(prev).filter((id) => validIds.has(id)));
      if (next.size === prev.size) {
        return prev;
      }
      return next;
    });
  }, [codingLineIds, selectedBulkLineIds.size]);
  const apPostingCatalogFields = apPostingErpCatalogQuery.data?.fields || [];
  const apPostingConfigByFieldId = new Map(
    (apPostingErpConfigQuery.data || []).map((config) => [config.field_definition_id, config]),
  );
  const enabledApPostingFieldIds = new Set(
    (apPostingErpConfigQuery.data || [])
      .filter((config) => config.enabled)
      .map((config) => config.field_definition_id),
  );
  const buildApPostingConfigPayload = useCallback((
    fieldId: string,
    updates: Partial<TenantErpFieldConfigItem>,
  ): TenantErpFieldConfigItem[] => {
    return apPostingCatalogFields.map((field, index) => {
      const existing = apPostingConfigByFieldId.get(field.id);
      const base: TenantErpFieldConfigItem = {
        field_definition_id: field.id,
        surface: 'ap_posting',
        enabled: existing?.enabled ?? false,
        display_order: existing?.display_order ?? index,
        required: existing?.required ?? field.required_default,
        memory_enabled: existing?.memory_enabled ?? false,
        automation_mode: existing?.automation_mode ?? 'off',
        extra: existing?.extra ?? {},
      };
      if (field.id !== fieldId) {
        return base;
      }
      return {
        ...base,
        ...updates,
        enabled: true,
      };
    });
  }, [apPostingCatalogFields, apPostingConfigByFieldId]);

  const handleAddApPostingErpField = useCallback(async (fieldId: string, target: 'header' | 'line' | 'header_only') => {
    if (!fieldId) {
      return;
    }
    await updateErpFieldConfig.mutateAsync(buildApPostingConfigPayload(fieldId, {
      enabled: true,
    }));
    if (target === 'header') {
      setSelectedHeaderErpFieldId('');
    } else if (target === 'line') {
      setSelectedLineErpFieldId('');
    } else {
      setSelectedHeaderOnlyErpFieldId('');
    }
    await Promise.all([
      apPostingErpConfigQuery.refetch(),
      lineItemsQuery.refetch(),
    ]);
  }, [apPostingErpConfigQuery, buildApPostingConfigPayload, lineItemsQuery, updateErpFieldConfig]);

  const availableTaxCodes = lineItemsData?.available_tax_codes || [];
  const dynamicBillFields = lineItemsData?.dynamic_erp_fields?.fields || [];
  const dynamicBillHeaderFields = dynamicBillFields.filter((config) =>
    config.field.value_scope === 'header' && isReviewErpField(config.field),
  );
  const dynamicBillLineFields = dynamicBillFields.filter((config) =>
    config.field.value_scope === 'line' && isReviewErpField(config.field),
  );
  const availableReviewErpFields = getAvailableReviewErpFields(
    apPostingCatalogFields,
    enabledApPostingFieldIds,
  );
  const availableHeaderErpFields = availableReviewErpFields.filter((field) => field.value_scope === 'header');
  const availableLineErpFields = availableReviewErpFields.filter((field) => field.value_scope === 'line');
  const availableHeaderOnlyErpFields = availableLineErpFields.filter(isHeaderOnlyReviewErpField);
  const dynamicLineValuesByKey = new Map<string, ErpFieldValue>();
  dynamicErpValues.forEach((value) => addDynamicValueLookup(dynamicLineValuesByKey, value));
  const bulkPostingLineFieldOptions = useMemo(() => {
    const options: Array<{ key: BulkPostingLineFieldKey; label: string; config?: typeof dynamicBillLineFields[number] }> = [
      { key: 'item', label: 'Item' },
      { key: 'category', label: 'Category / Account' },
    ];
    dynamicBillLineFields.forEach((config) => {
      options.push({
        key: config.field.field_key,
        label: config.field.label,
        config,
      });
    });
    return options;
  }, [dynamicBillLineFields]);
  const selectedBulkPostingLineField =
    bulkPostingLineFieldOptions.find((option) => option.key === bulkPostingLineFieldKey) ||
    bulkPostingLineFieldOptions[0];
  const headerOnlyDynamicLineFields = dynamicBillLineFields.filter((config) =>
    isHeaderOnlyReviewErpField(config.field),
  );
  const activeDynamicBillFieldKeys = new Set([
    ...dynamicBillHeaderFields.map((config) => config.field.field_key),
    ...(effectiveHeaderOnly
      ? headerOnlyDynamicLineFields.map((config) => config.field.field_key)
      : dynamicBillLineFields.map((config) => config.field.field_key)),
  ]);
  const pushableDynamicBillValues = dynamicErpValues.filter((value) =>
    activeDynamicBillFieldKeys.has(value.field_key),
  );
  useEffect(() => {
    if (!selectedBulkPostingLineField && bulkPostingLineFieldOptions[0]) {
      setBulkPostingLineFieldKey(bulkPostingLineFieldOptions[0].key);
    }
  }, [bulkPostingLineFieldOptions, selectedBulkPostingLineField]);

  const handleBulkDynamicLineApply = useCallback((config: typeof dynamicBillLineFields[number], template: {
    value: any;
    display_value: string;
    reference_option_id: string | null;
  }) => {
    if (selectedBulkLineIds.size === 0) {
      return;
    }
    const selectedLines = lineItems.filter((item) => selectedBulkLineIds.has(item.ap_line_id));
    const selectedKeys = new Set(
      selectedLines.flatMap((item) => lineValueKeys(config.field.field_key, item.ap_line_id, item.line_number)),
    );
    setDynamicErpValues((prev) => [
      ...prev.filter((value) => (
        value.field_key !== config.field.field_key ||
        !erpValueLineKeys(value).some((key) => selectedKeys.has(key))
      )),
      ...selectedLines.map((item) => ({
        field_definition_id: config.field_definition_id,
        field_key: config.field.field_key,
        line_id: item.ap_line_id,
        value: template.value,
        display_value: template.display_value,
        reference_option_id: template.reference_option_id,
        source: 'manual',
        evidence: { line_number: item.line_number },
      })),
    ]);
  }, [lineItems, selectedBulkLineIds]);

  const handleBulkDynamicLineClear = useCallback((config: typeof dynamicBillLineFields[number]) => {
    if (selectedBulkLineIds.size === 0) {
      return;
    }
    const selectedLines = lineItems.filter((item) => selectedBulkLineIds.has(item.ap_line_id));
    const selectedKeys = new Set(
      selectedLines.flatMap((item) => lineValueKeys(config.field.field_key, item.ap_line_id, item.line_number)),
    );
    setDynamicErpValues((prev) => prev.filter((value) => (
      value.field_key !== config.field.field_key ||
      !erpValueLineKeys(value).some((key) => selectedKeys.has(key))
    )));
  }, [lineItems, selectedBulkLineIds]);

  const handleBulkPostingFieldClear = useCallback(() => {
    if (!selectedBulkPostingLineField || selectedBulkLineIds.size === 0) {
      return;
    }
    if (selectedBulkPostingLineField.key === 'item') {
      handleBulkItemClear();
      return;
    }
    if (selectedBulkPostingLineField.key === 'category') {
      handleBulkAccountClear();
      return;
    }
    if (selectedBulkPostingLineField.config) {
      handleBulkDynamicLineClear(selectedBulkPostingLineField.config);
    }
  }, [
    handleBulkAccountClear,
    handleBulkDynamicLineClear,
    handleBulkItemClear,
    selectedBulkLineIds.size,
    selectedBulkPostingLineField,
  ]);

  const visibleDynamicHeaderFieldCount = dynamicBillHeaderFields.length;
  const editableDynamicBillPayload = lineItemsData?.dynamic_erp_fields
    ? {
        ...lineItemsData.dynamic_erp_fields,
        fields: dynamicBillHeaderFields,
      }
    : undefined;
  const linkedCount = effectiveHeaderOnly
    ? (headerOnlyAccountRef ? lineItems.length : 0)
    : lineItems.filter((item) =>
        getLinePostingType(item) === 'category'
          ? Boolean(getEffectiveErpAccountId(item.ap_line_id, item.erp_account_id))
          : getEffectiveErpItemId(item.ap_line_id, item.erp_item_id)
      ).length;
  const subtotal = lineItems.reduce(
    (sum, item) => sum + (typeof item.total === 'number' ? item.total : parseFloat(String(item.total)) || 0),
    0,
  );
  const netTotal = applyDiscount ? subtotal - discountedAmount : subtotal;
  const isSubmitting = approveMutation.isPending || correctMutation.isPending || discardMutation.isPending;
  const missingErpConnection = erpConnections.some((connection) => connection.status === 'active') && !selectedErpConnectionId;
  const selectedPo = invoiceMatchDetails?.linked_po || invoiceMatchDetails?.selected_po;
  const selectedPoLabel = selectedPo?.po_number ? String(selectedPo.po_number) : null;
  const isEmbedded = variant === 'embedded';

  return (
    <div
      className={
        isEmbedded
          ? 'rounded-lg border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800 overflow-hidden'
          : 'fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50'
      }
    >
      <div
        className={
          isEmbedded
            ? 'flex flex-col'
            : 'bg-white dark:bg-gray-800 rounded-lg shadow-xl w-[90vw] max-w-5xl mx-4 max-h-[95vh] overflow-hidden flex flex-col'
        }
      >
        {/* Header */}
        <div className="border-b border-gray-200 dark:border-gray-700 px-6 py-4 flex items-center justify-between flex-shrink-0">
          <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">
            Review AP Posting
          </h2>
          {!isEmbedded && (
            <button
              onClick={onClose}
              className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
              title="Close"
            >
              <X className="h-5 w-5" />
            </button>
          )}
        </div>

        {/* Scrollable Content */}
        <div className={isEmbedded ? 'px-6 py-4 space-y-5' : 'flex-1 overflow-y-auto px-6 py-4 space-y-5'}>
          {erpConnections.filter((connection) => connection.status === 'active').length > 0 && (
            <div className="flex items-center gap-3 border-b border-gray-200 pb-4 dark:border-gray-700">
              <label htmlFor={`erp-connection-${posting.id}`} className="text-sm font-medium text-gray-700 dark:text-gray-300">
                ERP company
              </label>
              <SearchableSelect
                id={`erp-connection-${posting.id}`}
                value={selectedErpConnectionId}
                disabled={isPostedCorrection}
                onChange={(value) => {
                  setSelectedErpConnectionId(value);
                  setItemSelections(new Map(
                    (lineItemsData?.line_items || []).map((line) => [
                      line.ap_line_id,
                      { erp_item_id: '', erp_item_name: '' },
                    ]),
                  ));
                  setAccountSelections(new Map(
                    (lineItemsData?.line_items || []).map((line) => [
                      line.ap_line_id,
                      { account_id: '', account_name: '' },
                    ]),
                  ));
                  setCustomerProjectId('');
                  setCustomerProjectName('');
                  setHeaderOnlyAccountRef('');
                  setHeaderOnlyAccountName('');
                  setDiscountAccountRef('');
                  setDiscountAccountName('');
                  setSelectedTaxCodeRef('');
                  setDynamicErpValues([]);
                  setSelectedHeaderErpFieldId('');
                  setSelectedLineErpFieldId('');
                  setSelectedHeaderOnlyErpFieldId('');
                }}
                options={[
                  { id: '', label: 'Choose ERP company' },
                  ...erpConnections.filter((connection) => connection.status === 'active').map((connection) => ({
                    id: connection.id,
                    label: connection.display_name || connection.external_company_name || `${connection.erp_type} ${connection.realm_id}`,
                  })),
                ]}
                placeholder="Choose ERP company"
                clearable={false}
                className="min-w-[240px]"
              />
            </div>
          )}
          {isApprovalRequiredBeforeSubmission && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900 dark:border-amber-900 dark:bg-amber-950 dark:text-amber-200">
              This AP posting matches an approval chain and will enter approval once you submit your review.
            </div>
          )}

          <APPostingErrorAlert error={postingError} />

          {hasApprovalChainView && (
            <div className="flex items-center justify-between gap-4 rounded-lg border border-gray-200 bg-gray-50 p-3 dark:border-gray-700 dark:bg-gray-800">
              <div>
                <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                  {approvalStatus === 'approved'
                    ? 'Approval chain completed'
                    : approvalStatus === 'rejected'
                    ? 'Approval chain rejected'
                    : 'Approval chain in progress'}
                </p>
                <p className="text-xs text-gray-600 dark:text-gray-400">
                  Toggle between the AP posting review and the approval history for this posting.
                </p>
              </div>
              <div className="inline-flex rounded-lg border border-gray-200 bg-white p-1 dark:border-gray-700 dark:bg-gray-900">
                <button
                  type="button"
                  onClick={() => setShowApprovalChain(false)}
                  className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
                    !showApprovalChain
                      ? 'bg-blue-600 text-white'
                      : 'text-gray-600 hover:text-gray-900 dark:text-gray-300 dark:hover:text-gray-100'
                  }`}
                >
                  Review
                </button>
                <button
                  type="button"
                  onClick={() => setShowApprovalChain(true)}
                  className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
                    showApprovalChain
                      ? 'bg-blue-600 text-white'
                      : 'text-gray-600 hover:text-gray-900 dark:text-gray-300 dark:hover:text-gray-100'
                  }`}
                >
                  Approval Progress
                </button>
              </div>
            </div>
          )}

          {showApprovalChain && approvalRequestId && (
            <ApprovalChainReviewUI
              approvalRequestId={approvalRequestId}
              extractionId={posting.id}
              onApprovalComplete={handleApprovalChainComplete}
              onRejected={handleApprovalChainRejected}
              onClose={onClose}
              entityLabel="AP posting"
              entityTitle="AP Posting"
            />
          )}

          {!showApprovalChain && (
            <>
              {hasApprovalChainView && (
                <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 text-sm text-blue-800 dark:border-blue-900 dark:bg-blue-950 dark:text-blue-200">
              {approvalStatus === 'approved'
                    ? 'This AP posting completed its approval chain. You can review the posting details here and switch to the approval history panel at any time.'
                    : approvalStatus === 'rejected'
                    ? 'This AP posting was rejected in its approval chain. You can review the posting details here and switch to the approval history panel at any time.'
                    : 'This AP posting is currently in an approval chain. You can review the posting details here and switch to the approval progress panel at any time.'}
                </div>
              )}

              {hasDiscrepancyTab && (
                <div className="inline-flex rounded-lg border border-gray-200 bg-white p-1 dark:border-gray-700 dark:bg-gray-900">
                  <button
                    type="button"
                    onClick={() => setActiveReviewTab('posting')}
                    className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
                      activeReviewTab === 'posting'
                        ? 'bg-blue-600 text-white'
                        : 'text-gray-600 hover:text-gray-900 dark:text-gray-300 dark:hover:text-gray-100'
                    }`}
                  >
                    Posting Review
                  </button>
                  <button
                    type="button"
                    onClick={() => setActiveReviewTab('discrepancies')}
                    className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
                      activeReviewTab === 'discrepancies'
                        ? 'bg-blue-600 text-white'
                        : 'text-gray-600 hover:text-gray-900 dark:text-gray-300 dark:hover:text-gray-100'
                    }`}
                  >
                    Match Discrepancies ({invoiceDiscrepancies.length})
                  </button>
                </div>
              )}

              {activeReviewTab === 'discrepancies' ? (
                <div className="space-y-4 rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-900">
                  <div className="space-y-1">
                    <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                      Discrepancy Comparison Across Matched Documents
                    </h3>
                    <p className="text-xs text-gray-600 dark:text-gray-400">
                      Invoice {posting.invoice_num}
                      {selectedPoLabel ? ` · PO ${selectedPoLabel}` : ''}
                    </p>
                  </div>

                  {invoiceMatchDetailsQuery.isLoading ? (
                    <div className="text-center py-8">
                      <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-gray-900 dark:border-gray-100"></div>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">Loading discrepancy details...</p>
                    </div>
                  ) : invoiceDiscrepancies.length === 0 ? (
                    <div className="rounded border border-green-200 bg-green-50 p-3 text-sm text-green-800 dark:border-green-900 dark:bg-green-950 dark:text-green-200">
                      No discrepancies were found for the latest matched PO/receipt set.
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {invoiceDiscrepancies.map((variance, index) => {
                        const breakdown = buildDiscrepancyBreakdown(variance);
                        const lineKey = variance.line_number !== undefined && variance.line_number !== null
                          ? String(variance.line_number)
                          : '';
                        const allocation = lineKey ? lineAllocationByInvoiceLine.get(lineKey) : undefined;
                        const severity = String(variance.severity || '').toLowerCase();
                        const severityClass =
                          severity === 'critical'
                            ? 'bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-200'
                            : severity === 'error'
                            ? 'bg-orange-100 text-orange-700 dark:bg-orange-950 dark:text-orange-200'
                            : 'bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-200';
                        return (
                          <div key={`${String(variance.type || 'variance')}-${index}`} className="rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
                            <div className="flex flex-wrap items-center gap-2">
                              <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                                {formatDiscrepancyLabel(String(variance.type || 'discrepancy'))}
                              </p>
                              <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${severityClass}`}>
                                {severity || 'warning'}
                              </span>
                              {lineKey ? (
                                <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-700 dark:bg-gray-700 dark:text-gray-200">
                                  Invoice line {lineKey}
                                </span>
                              ) : (
                                <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-700 dark:bg-gray-700 dark:text-gray-200">
                                  Header-level
                                </span>
                              )}
                            </div>

                            {allocation ? (
                              <p className="mt-2 text-xs text-gray-600 dark:text-gray-400">
                                Invoice: {String(allocation.invoice_line_description || '-')} · PO line {String(allocation.po_line_number || '-')}
                              </p>
                            ) : null}

                            {breakdown.rows.length > 0 ? (
                              <div className="mt-3 overflow-x-auto">
                                <table className="w-full text-xs">
                                  <thead>
                                    <tr className="border-b border-gray-200 text-left text-gray-500 dark:border-gray-700 dark:text-gray-400">
                                      <th className="py-2 pr-3 font-medium">Metric</th>
                                      <th className="py-2 pr-3 font-medium">Invoice</th>
                                      <th className="py-2 pr-3 font-medium">PO</th>
                                      <th className="py-2 pr-3 font-medium">Receipt</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {breakdown.rows.map((row, rowIndex) => (
                                      <tr key={`${row.metric}-${rowIndex}`} className="border-b border-gray-100 last:border-b-0 dark:border-gray-800">
                                        <td className="py-2 pr-3 font-medium text-gray-800 dark:text-gray-200">{row.metric}</td>
                                        <td className="py-2 pr-3 text-gray-700 dark:text-gray-300">{row.invoice || '—'}</td>
                                        <td className="py-2 pr-3 text-gray-700 dark:text-gray-300">{row.po || '—'}</td>
                                        <td className="py-2 pr-3 text-gray-700 dark:text-gray-300">{row.receipt || '—'}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            ) : null}

                            {breakdown.extras.length > 0 ? (
                              <div className="mt-3 grid gap-2 sm:grid-cols-2">
                                {breakdown.extras.map((extra) => (
                                  <div key={extra.key} className="rounded border border-gray-200 bg-gray-50 px-2 py-1.5 text-xs dark:border-gray-700 dark:bg-gray-900">
                                    <p className="font-medium text-gray-700 dark:text-gray-300">{extra.key}</p>
                                    <p className="text-gray-600 dark:text-gray-400">{extra.value}</p>
                                  </div>
                                ))}
                              </div>
                            ) : null}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              ) : (
                <>
          {isPendingApprovalChain && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900 dark:border-amber-900 dark:bg-amber-950 dark:text-amber-200">
              You can still update line-item mappings, customer/project, discount selection, and header-only bill mode while this approval step is pending.
            </div>
          )}

          {/* Posting Details */}
          <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 space-y-3">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label
                  htmlFor={`invoice-number-${posting.id}`}
                  className="text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase"
                >
                  Invoice number
                </label>
                <input
                  id={`invoice-number-${posting.id}`}
                  type="text"
                  value={invoiceNumber}
                  onChange={(event) => setInvoiceNumber(event.target.value)}
                  disabled={areReviewFieldsLocked}
                  maxLength={100}
                  className="mt-1 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-lg font-bold text-gray-900 disabled:cursor-not-allowed disabled:opacity-60 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
                />
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  This updates the linked invoice record too.
                </p>
                {isQuickBooksOnline && (
                  <p className={`mt-1 text-xs ${invoiceNumber.trim().length > 21 ? 'text-red-600 dark:text-red-400' : 'text-gray-500 dark:text-gray-400'}`}>
                    {invoiceNumber.trim().length}/21 characters for the QuickBooks Online bill reference
                  </p>
                )}
              </div>
              <div>
                <p className="text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase">Vendor</p>
                <p className="text-lg font-bold text-gray-900 dark:text-gray-100">
                  {posting.vendor_name || posting.vendor_name_extracted || '-'}
                </p>
              </div>
            </div>

            <hr className="border-gray-300 dark:border-gray-600" />

            <div className="grid grid-cols-3 gap-4">
              <div>
                <p className="text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase">Invoice Total</p>
                <p className="text-lg font-bold text-gray-900 dark:text-gray-100">${posting.invoice_total.toFixed(2)}</p>
              </div>
              <div>
                <p className="text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase">Current Amount</p>
                <p className="text-lg font-bold text-gray-900 dark:text-gray-100">${posting.posting_amount.toFixed(2)}</p>
              </div>
              {isBillFieldEnabled('tax_total') && (
                <div>
                  <p className="text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase">Tax</p>
                  <p className="text-lg font-bold text-gray-900 dark:text-gray-100">${posting.tax_total.toFixed(2)}</p>
                </div>
              )}
            </div>
          </div>
          {canCustomizeBillMemo && (
            <div className="space-y-2 rounded-lg border border-gray-200 bg-gray-50 p-3 dark:border-gray-700 dark:bg-gray-800">
              <label
                htmlFor={`qbd-bill-memo-${posting.id}`}
                className="block text-sm font-medium text-gray-700 dark:text-gray-300"
              >
                QuickBooks Desktop bill memo
                <span className="ml-1 text-xs font-normal text-gray-500 dark:text-gray-400">(optional)</span>
              </label>
              <textarea
                id={`qbd-bill-memo-${posting.id}`}
                value={billMemo}
                onChange={(event) => setBillMemo(event.target.value)}
                disabled={areReviewFieldsLocked}
                maxLength={4095}
                rows={2}
                placeholder="Enter the memo to post on the QuickBooks Desktop bill"
                className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-gray-900 disabled:cursor-not-allowed disabled:opacity-60 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100"
              />
            </div>
          )}
          {/* Tax Code Selection */}
          {hasErpIntegration && availableTaxCodes.length > 0 && (
            <div className="space-y-2 rounded-lg border border-gray-200 bg-gray-50 p-3 dark:border-gray-700 dark:bg-gray-800">
              <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Tax code refs <span className="text-xs font-normal text-gray-500 dark:text-gray-400">(optional override)</span>
              </p>
              <SearchableSelect
                value={selectedTaxCodeRef}
                disabled={areReviewFieldsLocked}
                onChange={setSelectedTaxCodeRef}
                options={[
                  { id: '', label: 'No tax code override' },
                  ...availableTaxCodes.map((taxCode) => ({
                    id: taxCode.id,
                    label: taxCode.name,
                    description: taxCode.description ?? undefined,
                  })),
                ]}
                placeholder="No tax code override"
                clearable={false}
              />
            </div>
          )}

          {hasErpIntegration && (visibleDynamicHeaderFieldCount > 0 || availableHeaderErpFields.length > 0) ? (
            <div className="space-y-3 rounded-lg border border-blue-100 bg-blue-50/60 p-3 dark:border-blue-900 dark:bg-blue-950/30">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <p className="text-sm font-medium text-gray-800 dark:text-gray-200">
                    Bill Coding Fields
                  </p>
                  <p className="mt-1 text-xs text-gray-600 dark:text-gray-400">
                    These fields are included with the ERP bill when the connected provider supports the target bill field.
                  </p>
                </div>
                {availableHeaderErpFields.length > 0 && (
                  <div className="flex flex-wrap items-center gap-2">
                    <SearchableSelect
                      value={selectedHeaderErpFieldId}
                      disabled={areReviewFieldsLocked || updateErpFieldConfig.isPending}
                      onChange={setSelectedHeaderErpFieldId}
                      options={[
                        { id: '', label: 'Header field...' },
                        ...availableHeaderErpFields.map((field) => ({ id: field.id, label: field.label })),
                      ]}
                      placeholder="Header field..."
                      clearable={false}
                      className="w-44"
                    />
                    <Button
                      type="button"
                      size="sm"
                      variant="secondary"
                      disabled={!selectedHeaderErpFieldId || areReviewFieldsLocked || updateErpFieldConfig.isPending}
                      onClick={() => handleAddApPostingErpField(selectedHeaderErpFieldId, 'header')}
                    >
                      Add Field
                    </Button>
                  </div>
                )}
              </div>
              {visibleDynamicHeaderFieldCount > 0 && editableDynamicBillPayload ? (
              <DynamicErpFieldsEditor
                payload={editableDynamicBillPayload}
                values={dynamicErpValues}
                onChange={setDynamicErpValues}
                surface="ap_posting"
                valueScope="header"
                disabled={areReviewFieldsLocked}
                showDescriptions
                showSourceBadge
              />
              ) : null}
            </div>
          ) : null}

          {/* Discount Toggle */}
          {hasDiscount && (
            <label className="flex items-center gap-3 cursor-pointer bg-blue-50 dark:bg-blue-900 border border-blue-200 dark:border-blue-700 rounded-lg p-3">
              <input
                type="checkbox"
                checked={applyDiscount}
                disabled={arePostingStructureControlsLocked}
                onChange={(e) => {
                  setApplyDiscount(e.target.checked);
                  if (!e.target.checked) {
                    setDiscountAccountRef('');
                    setDiscountAccountName('');
                  }
                }}
                className="w-4 h-4 rounded border-gray-300"
              />
              <div>
                <p className="font-semibold text-blue-900 dark:text-blue-100 text-sm">Apply Early Payment Discount</p>
                <p className="text-xs text-blue-700 dark:text-blue-300">
                  {discountPercent}% (${discountedAmount.toFixed(2)}) — deadline{' '}
                  {posting.discount_info?.discount_deadline
                    ? formatDate(posting.discount_info.discount_deadline)
                    : 'N/A'}
                </p>
              </div>
            </label>
          )}

          {/* Header-only bill toggle */}
          {showHeaderOnlyToggle && (
            <label className="flex items-center gap-3 cursor-pointer bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg p-3">
              <input
                type="checkbox"
                checked={headerOnly}
                disabled={arePostingStructureControlsLocked}
                onChange={(e) => {
                  setHeaderOnly(e.target.checked);
                  if (!e.target.checked) {
                    setHeaderOnlyAccountRef('');
                    setHeaderOnlyAccountName('');
                  }
                }}
                className="w-4 h-4 rounded border-gray-300"
              />
              <div>
                <p className="font-semibold text-gray-900 dark:text-gray-100 text-sm">Header-only bill</p>
                <p className="text-xs text-gray-600 dark:text-gray-400">
                  Post as a single account-based expense line instead of individual line items
                </p>
              </div>
            </label>
          )}

          {/* Header-only account picker */}
          {effectiveHeaderOnly && (
            <div className="bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-700 rounded-lg p-4 space-y-3">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <p className="text-sm font-semibold text-amber-950 dark:text-amber-100">
                    Header-only bill coding
                  </p>
                  <p className="mt-1 text-xs text-amber-800 dark:text-amber-200">
                    These selections apply to the single bill-total line that will be posted to ERP.
                  </p>
                </div>
                {hasErpIntegration && availableHeaderOnlyErpFields.length > 0 && (
                  <div className="flex flex-wrap items-center gap-2">
                    <SearchableSelect
                      value={selectedHeaderOnlyErpFieldId}
                      disabled={arePostingStructureControlsLocked || updateErpFieldConfig.isPending}
                      onChange={setSelectedHeaderOnlyErpFieldId}
                      options={[
                        { id: '', label: 'Header-only field...' },
                        ...availableHeaderOnlyErpFields.map((field) => ({ id: field.id, label: field.label })),
                      ]}
                      placeholder="Header-only field..."
                      clearable={false}
                      className="w-48"
                    />
                    <Button
                      type="button"
                      size="sm"
                      variant="secondary"
                      disabled={!selectedHeaderOnlyErpFieldId || arePostingStructureControlsLocked || updateErpFieldConfig.isPending}
                      onClick={() => handleAddApPostingErpField(selectedHeaderOnlyErpFieldId, 'header_only')}
                    >
                      Add Field
                    </Button>
                  </div>
                )}
              </div>
              <div className="space-y-2">
                <label
                  htmlFor={`header-only-line-description-${posting.id}`}
                  className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                >
                  Bill line description
                </label>
                <textarea
                  id={`header-only-line-description-${posting.id}`}
                  value={headerOnlyLineDescription}
                  onChange={(event) => setHeaderOnlyLineDescription(event.target.value)}
                  disabled={areReviewFieldsLocked}
                  maxLength={4095}
                  rows={2}
                  placeholder={`Invoice ${posting.invoice_num} total`}
                  className="w-full rounded-md border border-amber-300 bg-white px-3 py-2 text-gray-900 disabled:cursor-not-allowed disabled:opacity-60 dark:border-amber-700 dark:bg-gray-900 dark:text-gray-100"
                />
                <p className="text-xs text-amber-800 dark:text-amber-200">
                  This description is posted on the single bill-total line.
                </p>
              </div>
              <div className="space-y-2">
                <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Category / account for the bill total (${posting.posting_amount.toFixed(2)})
                </p>
                <QBAccountCell
                  erpConnectionId={selectedErpConnectionId || undefined}
                  billDetailProvider={selectedErpConnection?.erp_type}
                  value={headerOnlyAccountRef || null}
                  disabled={arePostingStructureControlsLocked}
                  onSelect={(accountId, accountName) => {
                    setHeaderOnlyAccountRef(accountId);
                    setHeaderOnlyAccountName(accountName);
                  }}
                  onClear={() => {
                    setHeaderOnlyAccountRef('');
                    setHeaderOnlyAccountName('');
                  }}
                />
              </div>
              {hasErpIntegration && (
                <div className="space-y-2 pt-2 border-t border-amber-200 dark:border-amber-700">
                  <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Project <span className="text-xs font-normal text-gray-500 dark:text-gray-400">(optional)</span>
                  </p>
                  <CustomerProjectPicker disabled={arePostingStructureControlsLocked} />
                  {lineItemsData?.customer_project_suggestions && lineItemsData.customer_project_suggestions.length > 0 && !customerProjectId && (
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      Suggested: {lineItemsData.customer_project_suggestions.slice(0, 2).map((s) => s.name).join(', ')}
                    </p>
                  )}
                </div>
              )}
              {headerOnlyDynamicLineFields.map((config) => {
                const currentValue = dynamicErpValues.find((value) => value.field_key === config.field.field_key);
                return (
                  <div key={config.id} className="space-y-2 pt-2 border-t border-amber-200 dark:border-amber-700">
                    <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      {config.field.label} <span className="text-xs font-normal text-gray-500 dark:text-gray-400">(optional)</span>
                    </p>
                    <DynamicErpLineFieldCell
                      config={config}
                      item={{ id: null, line_number: 1 }}
                      value={currentValue}
                      disabled={arePostingStructureControlsLocked}
                      lineIdOverride={null}
                      onChange={updateHeaderOnlyDynamicErpValue}
                    />
                  </div>
                );
              })}
              {applyDiscount && hasDiscount && (
                <div className="space-y-2 pt-2 border-t border-amber-200 dark:border-amber-700">
                  <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Discount account (-${discountedAmount.toFixed(2)})
                  </p>
                  <QBAccountCell
                    erpConnectionId={selectedErpConnectionId || undefined}
                    billDetailProvider={selectedErpConnection?.erp_type}
                    value={discountAccountRef || null}
                    disabled={arePostingStructureControlsLocked}
                    onSelect={(accountId, accountName) => {
                      setDiscountAccountRef(accountId);
                      setDiscountAccountName(accountName);
                    }}
                    onClear={() => {
                      setDiscountAccountRef('');
                      setDiscountAccountName('');
                    }}
                  />
                </div>
              )}
            </div>
          )}

          {/* Line Items with ERP Item Linking */}
          <div className="space-y-2">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                Line Items ({lineItems.length})
              </h3>
              <div className="flex flex-wrap items-center gap-2">
                {!effectiveHeaderOnly && hasErpIntegration && availableLineErpFields.length > 0 && (
                  <>
                    <SearchableSelect
                      value={selectedLineErpFieldId}
                      disabled={areReviewFieldsLocked || updateErpFieldConfig.isPending}
                      onChange={setSelectedLineErpFieldId}
                      options={[
                        { id: '', label: 'Line column...' },
                        ...availableLineErpFields.map((field) => ({ id: field.id, label: field.label })),
                      ]}
                      placeholder="Line column..."
                      clearable={false}
                      className="w-44"
                    />
                    <Button
                      type="button"
                      size="sm"
                      variant="secondary"
                      disabled={!selectedLineErpFieldId || areReviewFieldsLocked || updateErpFieldConfig.isPending}
                      onClick={() => handleAddApPostingErpField(selectedLineErpFieldId, 'line')}
                    >
                      Add Column
                    </Button>
                  </>
                )}
                {!effectiveHeaderOnly && (
                  <span className={`text-xs px-2 py-1 rounded font-medium ${
                    allLinesLinked
                      ? 'bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300'
                      : 'bg-yellow-100 dark:bg-yellow-900 text-yellow-700 dark:text-yellow-300'
                  }`}>
                    {linkedCount}/{lineItems.length} linked
                  </span>
                )}
              </div>
            </div>
            {!effectiveHeaderOnly && codingLineIds.length > 0 && (
              <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 dark:border-gray-700 dark:bg-gray-800">
                <div className="flex flex-wrap items-center gap-3">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-gray-800 dark:text-gray-200">
                      {selectedCodingCount} selected
                    </span>
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedBulkLineIds(() => {
                          if (selectedCodingCount === codingLineIds.length) {
                            return new Set();
                          }
                          return new Set(codingLineIds);
                        });
                      }}
                      disabled={areReviewFieldsLocked}
                      className="text-xs font-medium text-blue-600 hover:text-blue-700 disabled:cursor-not-allowed disabled:opacity-60 dark:text-blue-300 dark:hover:text-blue-200"
                    >
                      {allCodingSelected ? 'Clear all' : 'Select all lines'}
                    </button>
                    <button
                      type="button"
                      onClick={() => setSelectedBulkLineIds(new Set())}
                      disabled={areReviewFieldsLocked}
                      className="text-xs font-medium text-gray-600 hover:text-gray-800 disabled:cursor-not-allowed disabled:opacity-60 dark:text-gray-300 dark:hover:text-gray-100"
                    >
                      Clear selection
                    </button>
                  </div>
                  <div className="h-5 w-px bg-gray-300 dark:bg-gray-700" />
                  <SearchableSelect
                    value={selectedBulkPostingLineField?.key || ''}
                    disabled={areReviewFieldsLocked}
                    onChange={setBulkPostingLineFieldKey}
                    options={bulkPostingLineFieldOptions.map((option) => ({ id: option.key, label: option.label }))}
                    placeholder="Bulk field"
                    clearable={false}
                    className="w-48"
                  />
                  <div className="min-w-[240px] flex-1 sm:flex-none">
                    {selectedBulkPostingLineField?.key === 'item' ? (
                      <QBItemCell
                        erpConnectionId={selectedErpConnectionId || undefined}
                        value={null}
                        disabled={areReviewFieldsLocked || selectedCodingCount === 0}
                        onSelect={handleBulkItemSelect}
                        onClear={() => undefined}
                      />
                    ) : selectedBulkPostingLineField?.key === 'category' ? (
                      <QBAccountCell
                        erpConnectionId={selectedErpConnectionId || undefined}
                        billDetailProvider={selectedErpConnection?.erp_type}
                        value={null}
                        disabled={areReviewFieldsLocked || selectedCodingCount === 0}
                        onSelect={handleBulkAccountSelect}
                        onClear={() => undefined}
                      />
                    ) : selectedBulkPostingLineField?.config ? (
                      <DynamicErpBulkFieldPicker
                        config={selectedBulkPostingLineField.config}
                        disabled={areReviewFieldsLocked || selectedCodingCount === 0}
                        onApply={(template) => handleBulkDynamicLineApply(selectedBulkPostingLineField.config!, template)}
                      />
                    ) : null}
                  </div>
                  <button
                    type="button"
                    onClick={handleBulkPostingFieldClear}
                    disabled={areReviewFieldsLocked || selectedCodingCount === 0 || !selectedBulkPostingLineField}
                    className="rounded border border-gray-300 px-2 py-1 text-xs font-medium text-gray-700 hover:border-gray-400 hover:text-gray-900 disabled:cursor-not-allowed disabled:opacity-60 dark:border-gray-600 dark:text-gray-200 dark:hover:border-gray-500 dark:hover:text-gray-100"
                  >
                    Clear field
                  </button>
                </div>
              </div>
            )}

            {isLoadingLineItems ? (
              <div className="text-center py-8">
                <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-gray-900 dark:border-gray-100"></div>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">Loading line items...</p>
              </div>
            ) : lineItems.length > 0 ? (
              <div className="border border-gray-200 dark:border-gray-600 rounded-lg">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600">
                      {!effectiveHeaderOnly && codingLineIds.length > 0 && (
                        <th className="px-3 py-2 text-left text-gray-700 dark:text-gray-300 font-medium">
                          <input
                            type="checkbox"
                            checked={allCodingSelected}
                            onChange={() => {
                              if (areReviewFieldsLocked) {
                                return;
                              }
                              setSelectedBulkLineIds(() => {
                                if (selectedCodingCount === codingLineIds.length) {
                                  return new Set();
                                }
                                return new Set(codingLineIds);
                              });
                            }}
                            disabled={areReviewFieldsLocked}
                            className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                            aria-label="Select all coding lines"
                          />
                        </th>
                      )}
                      <th className="px-3 py-2 text-left text-gray-700 dark:text-gray-300 font-medium">Line</th>
                      {lineItemCols.map((col) => (
                        <th key={col} className="px-3 py-2 text-left text-gray-700 dark:text-gray-300 font-medium capitalize">
                          {col.replace(/_/g, ' ')}
                        </th>
                      ))}
                      {!effectiveHeaderOnly && (
                        <th className="px-3 py-2 text-left text-gray-700 dark:text-gray-300 font-medium">Line Type / Selection</th>
                      )}
                      {!effectiveHeaderOnly && dynamicBillLineFields.map((config) => (
                        <th key={config.id} className="px-3 py-2 text-left text-gray-700 dark:text-gray-300 font-medium">
                          {config.field.label}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {lineItems.map((item, index) => {
                      const effectiveId = getEffectiveErpItemId(item.ap_line_id, item.erp_item_id);
                      const effectiveAccountId = getEffectiveErpAccountId(item.ap_line_id, item.erp_account_id);
                      const linePostingType = getLinePostingType(item);
                      return (
                        <tr key={item.ap_line_id || `line-${index}`} className="border-b border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                          {!effectiveHeaderOnly && codingLineIds.length > 0 && (
                            <td className="px-3 py-2">
                              <input
                                type="checkbox"
                                checked={selectedBulkLineIds.has(item.ap_line_id)}
                                onChange={() => handleToggleBulkLine(item.ap_line_id)}
                                disabled={areReviewFieldsLocked || item.is_synthetic}
                                className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 disabled:cursor-not-allowed disabled:opacity-50"
                                aria-label={`Select line ${item.line_number}`}
                              />
                            </td>
                          )}
                          <td className="px-3 py-2 text-gray-900 dark:text-gray-100">{item.line_number}</td>

                          {lineItemCols.map((col) => {
                            const value = (item as unknown as Record<string, unknown>)[col];
                            return (
                              <td key={col} className="px-3 py-2 text-gray-900 dark:text-gray-100">
                                {col === 'description' ? (
                                  <span title={item.description} className="block truncate max-w-xs">{item.description}</span>
                                ) : typeof value === 'number' ? (
                                  col === 'unit_price' || col === 'total'
                                    ? `$${value.toFixed(2)}`
                                    : value.toFixed(2)
                                ) : value !== null && value !== undefined ? (
                                  String(value)
                                ) : (
                                  '-'
                                )}
                              </td>
                            );
                          })}

                          {!effectiveHeaderOnly && (
                            <td className="px-3 py-2">
                              <div className="min-w-[260px] space-y-2">
                                {!item.is_synthetic ? (
                                  <div className="inline-flex rounded-md border border-gray-300 bg-white p-0.5 text-xs font-medium dark:border-gray-600 dark:bg-gray-800">
                                    <button
                                      type="button"
                                      disabled={areReviewFieldsLocked}
                                      onClick={() => handleLinePostingTypeChange(item.ap_line_id, 'item')}
                                      className={`rounded px-2 py-1 transition-colors disabled:cursor-not-allowed disabled:opacity-60 ${
                                        linePostingType === 'item'
                                          ? 'bg-blue-600 text-white'
                                          : 'text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700'
                                      }`}
                                    >
                                      Item
                                    </button>
                                    <button
                                      type="button"
                                      disabled={areReviewFieldsLocked}
                                      onClick={() => handleLinePostingTypeChange(item.ap_line_id, 'category')}
                                      className={`rounded px-2 py-1 transition-colors disabled:cursor-not-allowed disabled:opacity-60 ${
                                        linePostingType === 'category'
                                          ? 'bg-blue-600 text-white'
                                          : 'text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700'
                                      }`}
                                    >
                                      Category
                                    </button>
                                  </div>
                                ) : (
                                  <span className="inline-flex rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800 dark:bg-amber-900/40 dark:text-amber-200">
                                    Category line
                                  </span>
                                )}
                                {linePostingType === 'category' ? (
                                  <QBAccountCell
                                    erpConnectionId={selectedErpConnectionId || undefined}
                                    billDetailProvider={selectedErpConnection?.erp_type}
                                    value={effectiveAccountId}
                                    disabled={areReviewFieldsLocked}
                                    onSelect={(accountId, accountName) => handleAccountSelect(item.ap_line_id, accountId, accountName)}
                                    onClear={() => handleAccountClear(item.ap_line_id)}
                                  />
                                ) : (
                                  <QBItemCell
                                    erpConnectionId={selectedErpConnectionId || undefined}
                                    value={effectiveId}
                                    disabled={areReviewFieldsLocked}
                                    onSelect={(itemId, itemName) => handleItemSelect(item.ap_line_id, itemId, itemName)}
                                    onClear={() => handleItemClear(item.ap_line_id)}
                                  />
                                )}
                              </div>
                            </td>
                          )}
                          {!effectiveHeaderOnly && dynamicBillLineFields.map((config) => {
                            const currentValue = getDynamicLineValue(
                              dynamicLineValuesByKey,
                              config.field.field_key,
                              item.ap_line_id,
                              item.line_number,
                            );
                            return (
                              <td key={`${item.ap_line_id}-${config.id}`} className="px-3 py-2">
                                <DynamicErpLineFieldCell
                                  config={config}
                                  item={{ ...item, id: item.ap_line_id }}
                                  value={currentValue}
                                  disabled={areReviewFieldsLocked}
                                  onChange={(value) => updateLineDynamicErpValue(value, item.line_number)}
                                />
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })}

                    {/* Discount line */}
                    {applyDiscount && hasDiscount && (
                      <tr className="border-b border-blue-200 dark:border-blue-700 bg-blue-50 dark:bg-blue-900/40">
                        <td className="px-3 py-2 text-blue-700 dark:text-blue-300 font-medium italic">Discount</td>
                        <td className="px-3 py-2 text-blue-700 dark:text-blue-300 italic">
                          Early Payment Discount ({discountPercent}%)
                        </td>
                        <td className="px-3 py-2 text-right text-blue-700 dark:text-blue-300">1</td>
                        <td className="px-3 py-2 text-right text-blue-700 dark:text-blue-300">
                          -${discountedAmount.toFixed(2)}
                        </td>
                        <td className="px-3 py-2 text-right font-semibold text-blue-700 dark:text-blue-300">
                          -${discountedAmount.toFixed(2)}
                        </td>
                      {!effectiveHeaderOnly && (
                        <td className="px-3 py-2">
                          <QBAccountCell
                            erpConnectionId={selectedErpConnectionId || undefined}
                            billDetailProvider={selectedErpConnection?.erp_type}
                            value={discountAccountRef || null}
                            disabled={arePostingStructureControlsLocked}
                            onSelect={(accountId, accountName) => {
                              setDiscountAccountRef(accountId);
                              setDiscountAccountName(accountName);
                            }}
                            onClear={() => {
                              setDiscountAccountRef('');
                              setDiscountAccountName('');
                            }}
                          />
                        </td>
                      )}
                      {!effectiveHeaderOnly && dynamicBillLineFields.map((config) => (
                        <td key={`discount-${config.id}`} className="px-3 py-2 text-xs text-gray-400 dark:text-gray-500">
                          -
                        </td>
                      ))}
                    </tr>
                  )}
                  </tbody>
                  <tfoot>
                    <tr className="bg-gray-50 dark:bg-gray-700 border-t-2 border-gray-300 dark:border-gray-600">
                      <td colSpan={lineItemCols.length + 1} className="px-3 py-2 text-right font-semibold text-gray-900 dark:text-gray-100">
                        {applyDiscount ? 'Net Total:' : 'Subtotal:'}
                      </td>
                      <td className="px-3 py-2 text-right font-semibold text-gray-900 dark:text-gray-100">
                        ${netTotal.toFixed(2)}
                      </td>
                      {!effectiveHeaderOnly && <td></td>}
                      {!effectiveHeaderOnly && dynamicBillLineFields.map((config) => (
                        <td key={`total-${config.id}`}></td>
                      ))}
                    </tr>
                  </tfoot>
                </table>
              </div>
            ) : (
              <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 text-center">
                <p className="text-sm text-gray-600 dark:text-gray-400">No line items found for this invoice</p>
              </div>
            )}
          </div>

          {/* Approval Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {isPostedCorrection ? 'Correction Notes (Optional)' : 'Approval Notes (Optional)'}
            </label>
            <textarea
              value={approvalNotes}
              onChange={(e) => setApprovalNotes(e.target.value)}
              placeholder={isPostedCorrection ? 'Add any notes about this correction...' : 'Add any notes about this approval...'}
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            />
          </div>

          {/* Discard Section */}
          {showDiscardForm && (
            <div className="bg-red-50 dark:bg-red-900 border border-red-200 dark:border-red-700 rounded-lg p-4 space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Reason for Discarding *
                </label>
                <textarea
                  value={discardReason}
                  onChange={(e) => setDiscardReason(e.target.value)}
                  placeholder="Explain why this AP posting should be discarded..."
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  required
                />
              </div>
              <p className="text-sm text-red-800 dark:text-red-200">
                This action cannot be undone. The invoice will be marked as discarded.
              </p>
              <div className="flex gap-3">
                <Button
                  onClick={handleDiscard}
                  disabled={isSubmitting || !discardReason.trim()}
                  className="bg-red-600 hover:bg-red-700"
                >
                  {discardMutation.isPending ? 'Processing...' : 'Confirm Discard'}
                </Button>
                <Button
                  onClick={() => { setShowDiscardForm(false); setDiscardReason(''); }}
                  variant="secondary"
                  disabled={discardMutation.isPending}
                >
                  Cancel
                </Button>
              </div>
            </div>
          )}
                </>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        {hasApprovalChainView && !showApprovalChain && !showDiscardForm && isPendingApprovalChain && (
          <div className="border-t border-gray-200 dark:border-gray-700 px-6 py-4 flex items-center justify-between flex-shrink-0 bg-white dark:bg-gray-800">
            <Button
              onClick={() => setShowDiscardForm(true)}
              variant="secondary"
              disabled={isSubmitting}
              className="text-red-600 hover:text-red-700 dark:text-red-400"
            >
              Reject
            </Button>
            <div className="flex gap-3">
              <Button onClick={onClose} variant="secondary" disabled={isSubmitting}>
                Cancel
              </Button>
              <Button
                onClick={handleApprove}
                disabled={isSubmitting || missingErpConnection}
                className="bg-green-600 hover:bg-green-700"
              >
                {approveMutation.isPending ? 'Submitting...' : 'Approve'}
              </Button>
            </div>
          </div>
        )}
        {hasApprovalChainView && !showApprovalChain && (!isPendingApprovalChain || showDiscardForm) && (
          <div className="border-t border-gray-200 dark:border-gray-700 px-6 py-4 flex justify-end flex-shrink-0 bg-white dark:bg-gray-800">
            <Button onClick={onClose} variant="secondary">
              Close
            </Button>
          </div>
        )}
        {!hasApprovalChainView && hasErpIntegration && !showDiscardForm && (
          <div className="border-t border-gray-200 dark:border-gray-700 px-6 py-4 flex items-center justify-between flex-shrink-0 bg-white dark:bg-gray-800">
            {isPostedCorrection ? <div /> : (
              <Button
                onClick={() => setShowDiscardForm(true)}
                variant="secondary"
                disabled={isSubmitting}
                className="text-red-600 hover:text-red-700 dark:text-red-400"
              >
                Discard
              </Button>
            )}
            <div className="flex gap-3">
              <Button onClick={onClose} variant="secondary" disabled={isSubmitting}>
                Cancel
              </Button>
              <Button
                onClick={handleApprove}
                disabled={isSubmitting || missingErpConnection || !allLinesLinked || (applyDiscount && !discountAccountRef)}
                className="bg-green-600 hover:bg-green-700"
              >
                {isSubmitting
                  ? 'Submitting...'
                  : isPostedCorrection
                    ? 'Push Correction'
                    : 'Approve'}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
